import {
	AppBar,
	Box,
	Container,
	Hidden,
	IconButton,
	PropTypes,
	Slide,
	SwipeableDrawer,
	Toolbar,
	useMediaQuery,
	useScrollTrigger,
	useTheme,
} from '@material-ui/core';
import {WithStyles, withStyles} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import MenuIcon from '@material-ui/icons/Menu';
import classNames from 'classnames';
import throttle from 'lodash/throttle';
import {useLayoutEffect, useState} from 'react';
import {WithTranslation, withTranslation} from 'react-i18next';
import {Link as RouterLink} from 'react-router-dom';

import HeaderLogo from 'src/Assets/Images/header-logo.png';
import AllLinksBlock from 'src/Components/Common/HeaderBar/NavigationMenu/AllLinksBlock';
import LinkedInProfileLink from 'src/Components/Common/Links/LinkedInProfileLink';
import XingProfileLink from 'src/Components/Common/Links/XingProfileLink';
import AporiaIcon from 'src/Components/Icons/AporiaIcon';
import EnumRoutes from 'src/Routes/EnumRoutes';
import {getRoutePathname} from 'src/Routes/getRoutePathname';
import EnvHelper from 'src/Utils/EnvHelper';

import LanguageMenu from './LanguageMenu/LanguageMenu';
import {headerBarStyles} from './headerBarStyles';

const HeaderBar = (props: THeaderBarProps) => {
	const theme = useTheme();
	const matches = useMediaQuery(theme.breakpoints.down('sm'));
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [topOffset, setTopOffset] = useState(window.scrollY);
	const linkProps = {language: props.i18n.language};

	useLayoutEffect(() => {
		const handleScroll = throttle(() => setTopOffset(window.scrollY), 320, {trailing: true});

		if (matches) {
			window.addEventListener('scroll', handleScroll, {passive: true, capture: false});
		}

		return () => {
			if (matches) {
				window.removeEventListener('scroll', handleScroll, {capture: false});
			}
		};
	}, [setTopOffset, matches]);

	const wrapperClasses = [props.classes.header];

	if (props.addHeaderOffset) {
		wrapperClasses.push('-with-offset');
	}

	return (
		<Slide appear={false} direction="down" in={!useScrollTrigger({target: window})}>
			<AppBar
				position={matches || props.isHeaderSticky ? 'sticky' : 'static'}
				elevation={0}
				color={!matches || topOffset < 200 ? props.headerColor : 'primary'}
				className={classNames(wrapperClasses)}
			>
				<Container maxWidth="lg">
					<Toolbar className={props.classes.toolbar} disableGutters>
						<span className={props.classes.logoAndLanguage}>
							<RouterLink to={getRoutePathname(EnumRoutes.HOME, linkProps)} className={props.classes.logoWrapper}>
								<img src={HeaderLogo} alt={props.t('common:aporia')} className={props.classes.logo} />
								<AporiaIcon className={props.classes.name} />
							</RouterLink>
							<Box className={props.classes.rightSide} component="span">
								<LanguageMenu />
								<Hidden smUp>
									<IconButton
										edge="start"
										className={props.classes.menuButton}
										color="inherit"
										aria-label="menu"
										onClick={() => setIsDrawerOpen(true)}
									>
										<MenuIcon />
									</IconButton>
								</Hidden>
							</Box>
						</span>
						<Hidden xsDown>
							<AllLinksBlock className={props.classes.link} component="div" />
						</Hidden>
						<Hidden smUp>
							<SwipeableDrawer
								open={isDrawerOpen}
								disableBackdropTransition={true}
								onClose={() => setIsDrawerOpen(false)}
								onOpen={() => setIsDrawerOpen(true)}
								anchor="top"
								PaperProps={{
									square: true,
								}}
								classes={{
									paper: props.classes.drawerPaper,
								}}
							>
								<span className={props.classes.logoAndLanguage}>
									<RouterLink to={getRoutePathname(EnumRoutes.HOME, linkProps)} className={props.classes.logoWrapper}>
										<img src={HeaderLogo} alt={props.t('common:aporia')} className={props.classes.logo} />
										<AporiaIcon className={props.classes.name} />
									</RouterLink>
									<Box className={classNames([props.classes.rightSide, '-is-in-menu'])} component="span">
										<LanguageMenu />
										<IconButton
											edge="start"
											className={classNames([props.classes.menuButton, props.classes.closeMenuButton])}
											color="inherit"
											aria-label="menu"
											onClick={() => setIsDrawerOpen(!isDrawerOpen)}
										>
											<CloseIcon />
										</IconButton>
									</Box>
								</span>
								<AllLinksBlock
									className={props.classes.linkMobile}
									component="div"
									wrapperClass={props.classes.linksMobileWrapper}
								/>
								<a href={`mailto:${EnvHelper.getAporiaEmail()}`} className={props.classes.emailLink}>
									{EnvHelper.getAporiaEmail()}
								</a>
								<div className={props.classes.linksMobileBottom}>
									<LinkedInProfileLink iconClassName={props.classes.socialIcon} />
									<XingProfileLink iconClassName={props.classes.socialIcon} />
								</div>
							</SwipeableDrawer>
						</Hidden>
					</Toolbar>
				</Container>
			</AppBar>
		</Slide>
	);
};

export interface IHeaderBarOwnProps {
	isHeaderSticky?: boolean;
	headerColor?: PropTypes.Color | 'transparent';
	addHeaderOffset?: boolean;
}

export type THeaderBarProps = IHeaderBarOwnProps & WithTranslation & WithStyles;

export default withStyles(headerBarStyles)(withTranslation('navigation')(HeaderBar));
